import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, TextArea, Segment, Icon, Confirm } from "semantic-ui-react";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { closeModal } from "../../../app/common/modals/modalSlice";
import { resolveComment, deleteComment, editComment } from "./docCommentsActions";
import { canManageComment, formatCommentDate } from "./docCommentsUtils";

export default function DocCommentViewModal({ comment }) {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const [isEditing, setIsEditing] = useState(false);
  const [editedComment, setEditedComment] = useState(comment.comment);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);

  const canManage = canManageComment(currentUserProfile, comment);

  const handleEdit = async () => {
    if (!editedComment.trim()) {
      return;
    }

    setLoading(true);
    try {
      await dispatch(editComment(comment.id, comment.documentId, editedComment.trim()));
      setIsEditing(false);
    } catch (error) {
      console.error("Error editing comment:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    setLoading(true);
    try {
      await dispatch(resolveComment(comment.id, comment.documentId));
      dispatch(closeModal());
    } catch (error) {
      console.error("Error resolving comment:", error);
    } finally {
      setLoading(false);
      setShowResolveConfirm(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await dispatch(deleteComment(comment.id, comment.documentId));
      dispatch(closeModal());
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleClose = () => {
    dispatch(closeModal());
  };



  return (
    <>
      <ModalWrapper size="small" header="Comment Details">
        <Segment>
          <div style={{ marginBottom: "15px" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
              <Icon name="user" color="blue" />
              <strong>
                {comment.createdBy.firstName} {comment.createdBy.lastName}
              </strong>
              <span style={{ marginLeft: "10px", color: "#666", fontSize: "0.9em" }}>
                ({comment.createdBy.role})
              </span>
            </div>
            <div style={{ color: "#666", fontSize: "0.9em", marginBottom: "10px" }}>
              <Icon name="clock" />
              Created: {formatCommentDate(comment.createdAt)} 
            </div>
            {comment.editedAt && (
              <div style={{ color: "#666", fontSize: "0.9em", marginBottom: "10px" }}>
                <Icon name="edit" />
                Last edited: {formatCommentDate(comment.editedAt)}
                {comment.editedBy && (
                  <span> by {comment.editedBy.firstName} {comment.editedBy.lastName}</span>
                )}
              </div>
            )}
          </div>

          {isEditing ? (
            <Form>
              <Form.Field>
                <label>Edit Comment</label>
                <TextArea
                  value={editedComment}
                  onChange={(e) => setEditedComment(e.target.value)}
                  rows={4}
                  maxLength={1000}
                />
                <div style={{ textAlign: "right", fontSize: "0.8em", color: "#666" }}>
                  {editedComment.length}/1000 characters
                </div>
              </Form.Field>
              <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                <Button
                  size="small"
                  onClick={() => {
                    setIsEditing(false);
                    setEditedComment(comment.comment);
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  primary
                  onClick={handleEdit}
                  loading={loading}
                  disabled={!editedComment.trim() || loading}
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          ) : (
            <div>
              <div style={{ 
                backgroundColor: "#f8f9fa", 
                padding: "15px", 
                borderRadius: "5px",
                marginBottom: "15px",
                border: "1px solid #e9ecef"
              }}>
                {comment.comment}
              </div>
              
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <Button
                      size="small"
                      icon="check"
                      content="Resolve"
                      color="green"
                      onClick={() => setShowResolveConfirm(true)}
                      disabled={loading}
                    />
                {canManage && (
                  <>
                    <Button
                      size="small"
                      icon="edit"
                      content="Edit"
                      color="blue"
                      onClick={() => setIsEditing(true)}
                      disabled={loading}
                    />
                    
                    <Button
                      size="small"
                      icon="trash"
                      content="Delete"
                      color="red"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={loading}
                    />
                  </>
                )}
                <Button
                  size="small"
                  content="Close"
                  onClick={handleClose}
                />
              </div>
            </div>
          )}
        </Segment>
      </ModalWrapper>

      <Confirm
        open={showResolveConfirm}
        header="Resolve Comment"
        content="Are you sure you want to resolve this comment? It will be marked as resolved and hidden from the document view."
        onCancel={() => setShowResolveConfirm(false)}
        onConfirm={handleResolve}
        confirmButton="Resolve"
      />

      <Confirm
        open={showDeleteConfirm}
        header="Delete Comment"
        content="Are you sure you want to delete this comment? This action cannot be undone."
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        confirmButton="Delete"
      />
    </>
  );
}
